import { ReactNode, useMemo } from "react";
import { ChartConfig } from "@/components/ui/chart";
import { getColor } from "@/components/radixColors";
import { Filter } from "@/v2/entities/explore/Explore.filter";
import { useExploreAggInteractions } from "@/v2/entities/explore/Explore.api";
import {
  ExploreInteractionType,
  exploreInteractionTypeToLabel,
} from "@/v2/entities/explore/Explore.model";
import { isExploreGroupByColumnType } from "@/v2/entities/explore/columns/ExploreColum.model";
import {
  normalizeValue,
  getGroupByColumnConfig,
  CHART_UNCLASSIFIED_LABEL,
} from "./explore-widgets-utils";

export type ChartItem = {
  value: string;
  volume: number;
  fill: string;
};

function getGroupByValue(
  result: Record<string, unknown>,
  groupBy: string
): string | number | boolean | null | undefined {
  const value = result[groupBy];
  if (
    value == undefined ||
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return value;
  }
  return undefined;
}

export function useExploreChart(
  source: ExploreInteractionType = "tickets",
  groupBy: string = "tenantId",
  filter?: Filter
): {
  items: ChartItem[];
  isLoading: boolean;
  totalVolume: number;
  chartConfig: ChartConfig;
  itemToLabelMap: Map<string, string>;
  labelFormatter?: (value?: string) => ReactNode;
} {
  const { data, isLoading } = useExploreAggInteractions({
    source,
    groupBy,
    filter,
  });

  const { items, itemToLabelMap, chartConfig, labelFormatter } = useMemo(() => {
    const chartConfig: ChartConfig = {};
    const items: ChartItem[] = [];
    const itemToLabelMap = new Map<string, string>();
    const isKnownGroupBy = isExploreGroupByColumnType(groupBy);
    const { labelFormatter: knownLabelFormatter, colorOverrides } = isKnownGroupBy
      ? getGroupByColumnConfig(groupBy)
      : {
          labelFormatter: (value?: string) => value ?? "",
          colorOverrides: undefined as Record<string, string> | undefined,
        };

    data?.results.forEach((result, index) => {
      const value = getGroupByValue(
        result as unknown as Record<string, unknown>,
        groupBy
      );
      if (value === undefined) {
        return;
      }

      const normalizedValue = normalizeValue(value);
      items.push({
        value: normalizedValue,
        volume: result.volume,
        fill: `var(--color-${normalizedValue})`,
      });

      if (groupBy === "tenantId") {
        const label = exploreInteractionTypeToLabel[source].plural;
        itemToLabelMap.set(normalizedValue, label);
        chartConfig[normalizedValue] = {
          label,
          color: getColor(index++),
        };
        return;
      }

      const displayLabel =
        value == null ? CHART_UNCLASSIFIED_LABEL : value.toString();
      itemToLabelMap.set(normalizedValue, displayLabel);

      chartConfig[normalizedValue] = {
        label:
          value == null
            ? CHART_UNCLASSIFIED_LABEL
            : knownLabelFormatter(value?.toString()),
        color: colorOverrides?.[normalizedValue] ?? getColor(index++),
      };
    });

    return {
      items,
      itemToLabelMap,
      chartConfig,
      labelFormatter: knownLabelFormatter,
    };
  }, [groupBy, source, data?.results]);

  const totalVolume = useMemo(() => {
    return items.reduce((acc, curr) => acc + curr.volume, 0) ?? 0;
  }, [items]);

  return {
    items,
    isLoading,
    totalVolume,
    chartConfig,
    itemToLabelMap,
    labelFormatter,
  };
}
